import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { ElfBaseStore } from '@/store/base-store.repository';
import { DynaDocsModelStatusRepositoryFactory } from '@/store/dac-general-params-state/dyna-docs-model-status-state/dyna-docs-model-status.repository';
import { DynaDocsModelStatusProps } from '@/store/dac-general-params-state/dyna-docs-model-status-state/dyna-docs-model-status.store';
import { DynaDocsModelStatusDto } from '@/store/dac-general-params-state/dyna-docs-model-status-state/dyna-docs-model-status.store';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { select } from '@ngneat/elf';
import { Subscription } from 'rxjs';
import { form } from '@/utils/form.utils';

@Injectable({
  providedIn: 'root',
})
export class DynaDocsModelStatusService implements OnDestroy {
  multiFormAccordionService = inject(MultiFormAccordionService);

  private storeSubscriptions: Subscription[] = [];

  initialize() {
    this.subscribeToDynamicFieldStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.subscribeToStores();
  }

  subscribeToDynamicFieldStores() {
    const stores = this.multiFormAccordionService.getAllStores();
    this.storeSubscriptions = stores.map(store => {
      return store.pipe(select(state => state)).subscribe();
    });
  }

  subscribeToStore(store?: ElfBaseStore) {
    const sub = store?.pipe(select(state => state)).subscribe();
    this.storeSubscriptions.push(sub!);
  }

  subscribeToStores() {
    this.subscribeToDynamicFieldStores();
    this.storeSubscriptions.push(
      this.multiFormAccordionService.newStore$.subscribe(store => {
        this.subscribeToStore(store);
      })
    );
    this.storeSubscriptions.push(this.multiFormAccordionService.storeRemoved$.subscribe());
  }

  getAllFieldDtos(): DynaDocsModelStatusDto[] {
    return this.multiFormAccordionService.getAllStores().map((store: ElfBaseStore<DynaDocsModelStatusProps>) => {
      const repo = DynaDocsModelStatusRepositoryFactory.create(store);
      return repo.get<DynaDocsModelStatusDto>('dto');
    });
  }

  validateAllFieldDtos(): boolean {
    let isValid = true;

    this.multiFormAccordionService.getAllStores().forEach((store: ElfBaseStore<DynaDocsModelStatusProps>, index) => {
      const repo = DynaDocsModelStatusRepositoryFactory.create(store);
      const dto = repo.get<DynaDocsModelStatusDto>('dto');

      if (!dto.dynaDocsModelId) {
        isValid = false;
        repo.setNested('errors', 'dynaDocsModelId', ['Le modèle de document est requis']);
        MultiFormAccordionService.openPanelIfClosed(`mfa-${index}`);
        form.scrollToInvalidElement();
      } else {
        repo.clearError('dynaDocsModelId');
      }
    });

    return isValid;
  }

  setFieldDto(response: any): void {
    const newStore = this.multiFormAccordionService._formFactory?.(response.id?.toString() || Math.random().toString(36).substring(7));
    if (!newStore) return;

    const root = this.multiFormAccordionService.root;
    if ('children' in root) {
      root.children.push({ store: newStore });
    }

    this.multiFormAccordionService.newStore$.next(newStore);

    const repo = DynaDocsModelStatusRepositoryFactory.create(newStore);
    repo.setFromResponse(response);
  }

  setFieldDtos(responses: any[]): void {
    responses.forEach(r => this.setFieldDto(r));
  }

  resetAllFields() {
    this.multiFormAccordionService.getAllStores().forEach(store => {
      const repo = DynaDocsModelStatusRepositoryFactory.create(store);
      repo.reset();
    });
  }

  ngOnDestroy() {
    this.storeSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
